import React, { useEffect } from "react";
import "@/App.css";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { Toaster } from "sonner";

import { useAuth } from "./admin/store";
import { useThemeInit, useTheme } from "./admin/theme";
import Layout from "./admin/Layout";
import Login from "./admin/pages/Login";
import Dashboard from "./admin/pages/Dashboard";
import Jobs from "./admin/pages/Jobs";
import Users from "./admin/pages/Users";
import UserDetail from "./admin/pages/UserDetail";
import Analytics from "./admin/pages/Analytics";
import Settings from "./admin/pages/Settings";
import Audit from "./admin/pages/Audit";
import Storage from "./admin/pages/Storage";
import Health from "./admin/pages/Health";
import Operations from "./admin/pages/Operations";
import IgValidation from "./admin/pages/IgValidation";
import Affiliates from "./admin/pages/Affiliates";
import AffiliateLayout from "./affiliate/Layout";
import { useAffAuth } from "./affiliate/store";
import AffLogin from "./affiliate/pages/Login";
import AffSignup from "./affiliate/pages/Signup";
import AffForgot from "./affiliate/pages/Forgot";
import AffReset from "./affiliate/pages/Reset";
import AffDashboard from "./affiliate/pages/Dashboard";
import AffCodes from "./affiliate/pages/Codes";
import AffTransactions from "./affiliate/pages/Transactions";
import AffPayouts from "./affiliate/pages/Payouts";
import AffProfile from "./affiliate/pages/Profile";
import Landing from "./landing/Landing";
import Terms from "./landing/Terms";

function Protected({ children }) {
  const { status, bootstrap } = useAuth();
  const location = useLocation();
  useThemeInit();

  useEffect(() => {
    if (status === "checking") bootstrap();
  }, [status, bootstrap]);

  if (status === "checking") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="font-mono text-xs text-muted-foreground animate-pulse">
          // authenticating
        </div>
      </div>
    );
  }
  if (status !== "authed") {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }
  return children;
}

function LoginRoute() {
  const { status, bootstrap } = useAuth();
  useEffect(() => {
    if (status === "checking") bootstrap();
  }, [status, bootstrap]);
  if (status === "authed") return <Navigate to="/admin" replace />;
  return <Login />;
}

function AffProtected({ children }) {
  const { status, bootstrap } = useAffAuth();
  const location = useLocation();

  useEffect(() => {
    if (status === "checking") bootstrap();
  }, [status, bootstrap]);

  if (status === "checking") {
    return (
      <div className="dark min-h-screen bg-black flex items-center justify-center">
        <div className="font-mono text-xs text-white/40 animate-pulse">
          // authenticating
        </div>
      </div>
    );
  }
  if (status !== "authed") {
    return <Navigate to="/affiliate/login" state={{ from: location }} replace />;
  }
  return children;
}

function AffPublicRoute({ children }) {
  const { status, bootstrap } = useAffAuth();
  useEffect(() => {
    if (status === "checking") bootstrap();
  }, [status, bootstrap]);
  if (status === "authed") return <Navigate to="/affiliate" replace />;
  return children;
}

function App() {
  const theme = useTheme((s) => s.theme);
  return (
    <>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/admin/login" element={<LoginRoute />} />
          <Route
            path="/affiliate/login"
            element={
              <AffPublicRoute>
                <AffLogin />
              </AffPublicRoute>
            }
          />
          <Route
            path="/affiliate/signup"
            element={
              <AffPublicRoute>
                <AffSignup />
              </AffPublicRoute>
            }
          />
          <Route
            path="/affiliate/claim"
            element={
              <AffPublicRoute>
                <AffSignup />
              </AffPublicRoute>
            }
          />
          <Route
            path="/affiliate/forgot"
            element={
              <AffPublicRoute>
                <AffForgot />
              </AffPublicRoute>
            }
          />
          <Route path="/affiliate/reset" element={<AffReset />} />
          <Route
            path="/affiliate"
            element={
              <AffProtected>
                <AffiliateLayout />
              </AffProtected>
            }
          >
            <Route index element={<AffDashboard />} />
            <Route path="codes" element={<AffCodes />} />
            <Route path="transactions" element={<AffTransactions />} />
            <Route path="payouts" element={<AffPayouts />} />
            <Route path="profile" element={<AffProfile />} />
          </Route>
          <Route
            path="/admin"
            element={
              <Protected>
                <Layout />
              </Protected>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="health" element={<Health />} />
            <Route path="operations" element={<Operations />} />
            <Route path="alerts" element={<Navigate to="/admin/operations" replace />} />
            <Route path="jobs" element={<Jobs />} />
            <Route path="users" element={<Users />} />
            <Route path="users/:userId" element={<UserDetail />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="settings" element={<Settings />} />
            <Route path="ig-validation" element={<IgValidation />} />
            <Route path="affiliates" element={<Affiliates />} />
            <Route path="audit" element={<Audit />} />
            <Route path="storage" element={<Storage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster
        theme={theme}
        position="top-right"
        toastOptions={{
          style: {
            fontFamily: "JetBrains Mono, monospace",
            fontSize: "12px",
          },
        }}
      />
    </>
  );
}

export default App;
